import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../AppContext";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import {
  getErrorToast,
  getSuccessToast,
} from "../../../common/infrastructure/utilities/toastUtility";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";
import { PaymentTypes } from "../../../common/enums";
import { IStudioSettingsModel } from "../../../common/models/IStudioSettingsModel";
import {
  getPaymentOptions,
  sortPaymentOptionsByTypeOrder,
} from "../../../common/utilities/paymentUtility";
import {
  GymCheckInAction,
  HISTORY_PAGE_SIZE,
  HistoryFilter,
  IGymCheckInPayload,
  IGymCheckInResult,
  IGymHistoryPage,
  IGymLastActionResponse,
  IGymStats,
  IGymUserSearchResult,
  IGymVisitViewModel,
  getMemberFullName,
} from "../models/gymModels";

const normalizeRfid = (raw: string) =>
  raw.trim().toUpperCase().replace(/\s+/g, "");

const formatDateForApi = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

// Returns Monday 00:00 of the current week.
const startOfThisWeek = () => {
  const d = new Date();
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, …
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

interface IHistoryQueryArgs {
  filter: HistoryFilter;
  customDateFrom: string | null;
  customDateTo: string | null;
}

const buildHistoryEndpoint = (
  page: number,
  args: IHistoryQueryArgs,
): string => {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(HISTORY_PAGE_SIZE));

  switch (args.filter) {
    case "today": {
      const today = formatDateForApi(new Date());
      params.set("dateFrom", today);
      params.set("dateTo", today);
      break;
    }
    case "thisWeek": {
      params.set("dateFrom", formatDateForApi(startOfThisWeek()));
      params.set("dateTo", formatDateForApi(new Date()));
      break;
    }
    case "dateRange": {
      if (args.customDateFrom) params.set("dateFrom", args.customDateFrom);
      if (args.customDateTo) params.set("dateTo", args.customDateTo);
      break;
    }
    case "inside":
      params.set("insideOnly", "true");
      break;
    case "all":
    default:
      break;
  }

  return `${endpointsMetadata.gym.history}?${params.toString()}`;
};

export const useGym = () => {
  const { selectedStudioId } = useAppContext();
  const { mutate, isLoading: isCheckInSubmitting } =
    useApiMutation<IGymCheckInResult>();
  const { mutate: revertMutate, isLoading: isReverting } = useApiMutation();

  const [query, setQuery] = useState("");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  const [pendingPaymentFor, setPendingPaymentFor] =
    useState<IGymUserSearchResult | null>(null);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);

  // ─── History filters / pagination ─────────────────────────────────

  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("today");
  const [customDateFrom, setCustomDateFrom] = useState<string | null>(null);
  const [customDateTo, setCustomDateTo] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  // Reset to first page whenever the filter or its date range changes.
  useEffect(() => {
    setHistoryPage(1);
  }, [historyFilter, customDateFrom, customDateTo]);

  // ─── Queries ──────────────────────────────────────────────────────

  // Pre-load all users for this studio (same pattern as cashdesk user search):
  // empty query returns first 50. The frontend filters locally for instant feedback.
  const { data: allUsers, refetch: refetchUsers } = useApiQuery<
    IGymUserSearchResult[]
  >({
    queryKey: [endpointsMetadata.gym.searchUsers, selectedStudioId],
    endpoint: `${endpointsMetadata.gym.searchUsers}?query=`,
    enabled: selectedStudioId > 0,
  });

  const historyEndpoint = useMemo(
    () =>
      buildHistoryEndpoint(historyPage, {
        filter: historyFilter,
        customDateFrom,
        customDateTo,
      }),
    [historyPage, historyFilter, customDateFrom, customDateTo],
  );

  const {
    data: history,
    refetch: refetchHistory,
    isLoading: isHistoryLoading,
  } = useApiQuery<IGymHistoryPage>({
    queryKey: [
      endpointsMetadata.gym.history,
      selectedStudioId,
      historyPage,
      historyFilter,
      customDateFrom,
      customDateTo,
    ],
    endpoint: historyEndpoint,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const { data: insideList, refetch: refetchInside } = useApiQuery<
    IGymVisitViewModel[]
  >({
    queryKey: [endpointsMetadata.gym.currentlyInside, selectedStudioId],
    endpoint: endpointsMetadata.gym.currentlyInside,
    enabled: selectedStudioId > 0,
  });

  const { data: gymStats, refetch: refetchStats } = useApiQuery<IGymStats>({
    queryKey: [endpointsMetadata.gym.stats, selectedStudioId],
    endpoint: endpointsMetadata.gym.stats,
    enabled: selectedStudioId > 0,
  });

  // Studio-scoped payment types — only the methods enabled in studio settings
  // should appear in the gym payment modal (mirrors cashdesk + arrival flow).
  const { data: studioSettings } = useApiQuery<IStudioSettingsModel>({
    queryKey: [endpointsMetadata.studio.getStudioSettings, selectedStudioId],
    endpoint: endpointsMetadata.studio.getStudioSettings,
    enabled: selectedStudioId > 0,
  });

  // Mirrors EventArrivalConfirmation: keep every studio-enabled payment type;
  // Plan is excluded here because the backend auto-applies it when the user
  // has an active plan (the modal only opens for the no-plan / paid path).
  const paymentOptions = useMemo(() => {
    const enabled = studioSettings?.paymentTypes;
    const filtered = getPaymentOptions()
      .filter((opt) => opt.id !== PaymentTypes.Plan)
      .filter(
        (opt) => !enabled || enabled.some((pt) => pt.paymentType === opt.id),
      );
    return sortPaymentOptionsByTypeOrder(
      filtered,
      enabled?.map((pt) => pt.paymentType) ?? [],
    );
  }, [studioSettings]);

  // Fires when a member is selected so the revert button knows whether
  // there is anything to undo. Returns { lastAction: null | { visitId, action } }.
  const {
    data: lastActionData,
    isFetching: isLastActionLoading,
    refetch: refetchLastAction,
  } = useApiQuery<IGymLastActionResponse>({
    queryKey: [
      endpointsMetadata.gym.lastActionForUser,
      selectedStudioId,
      selectedMemberId,
    ],
    endpoint: `${endpointsMetadata.gym.lastActionForUser}?userId=${selectedMemberId ?? 0}`,
    enabled: selectedStudioId > 0 && (selectedMemberId ?? 0) > 0,
    globalIsLoading: false,
  });

  const refreshAll = useCallback(() => {
    refetchUsers();
    refetchHistory();
    refetchInside();
    refetchStats();
    refetchLastAction();
  }, [
    refetchUsers,
    refetchHistory,
    refetchInside,
    refetchStats,
    refetchLastAction,
  ]);

  // ─── Derived state ────────────────────────────────────────────────

  const insideVisits = useMemo(() => insideList ?? [], [insideList]);
  const historyItems = useMemo(() => history?.items ?? [], [history]);
  const historyTotalCount = history?.totalCount ?? 0;
  const historyHasMore = history?.hasMore ?? false;
  const historyTotalPages = Math.max(
    1,
    Math.ceil(historyTotalCount / HISTORY_PAGE_SIZE),
  );

  const searchResults = useMemo(() => {
    if (!allUsers) return [] as IGymUserSearchResult[];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allUsers
      .filter((u) => {
        const full = getMemberFullName(u).toLowerCase();
        return (
          String(u.id).includes(q) ||
          full.includes(q) ||
          u.firstName.toLowerCase().includes(q) ||
          (u.lastName ?? "").toLowerCase().includes(q) ||
          (u.phoneNumber ?? "").toLowerCase().includes(q) ||
          (u.email ?? "").toLowerCase().includes(q) ||
          (u.rfidBandId ?? "").toLowerCase().includes(q)
        );
      })
      .slice(0, 8);
  }, [allUsers, query]);

  const selectedMember = useMemo(
    () => allUsers?.find((u) => u.id === selectedMemberId) ?? null,
    [allUsers, selectedMemberId],
  );

  const openVisitForSelected = useMemo(() => {
    if (!selectedMember) return null;
    return insideVisits.find((v) => v.userId === selectedMember.id) ?? null;
  }, [selectedMember, insideVisits]);

  const stats = useMemo(
    () => ({
      insideNow: gymStats?.insideCount ?? insideVisits.length,
      today: gymStats?.todayCount ?? 0,
    }),
    [gymStats, insideVisits.length],
  );

  // ─── Actions ──────────────────────────────────────────────────────

  const sendCheckIn = useCallback(
    (
      member: IGymUserSearchResult,
      paymentType?: PaymentTypes,
      pricePaid?: number,
    ) => {
      const payload: IGymCheckInPayload = {
        userId: member.id,
        paymentType: paymentType ?? null,
        pricePaid: pricePaid ?? null,
        subscriptionPlanId: member.activePlan?.subscriptionPlanId ?? null,
      };

      mutate(
        {
          endpoint: endpointsMetadata.gym.checkInOrOut,
          method: "post",
          payload,
          disableSuccessToast: true,
          invalidateKeys: [
            [endpointsMetadata.gym.searchUsers, selectedStudioId],
            [endpointsMetadata.gym.currentlyInside, selectedStudioId],
            [endpointsMetadata.gym.history, selectedStudioId],
            [endpointsMetadata.gym.stats, selectedStudioId],
          ],
        },
        {
          onSuccess: (result: IGymCheckInResult) => {
            if (result.action === GymCheckInAction.DuplicateIgnored) {
              getErrorToast(
                result.message ??
                  "Дублирано действие - моля изчакайте няколко секунди преди да направите тази операция.",
              );
              setIsPaymentSubmitting(false);
              return;
            }

            if (result.action === GymCheckInAction.PaymentRequired) {
              setPendingPaymentFor(member);
              getErrorToast(
                result.message ??
                  `${getMemberFullName(member)} няма активен план — избери плащане.`,
              );
              setIsPaymentSubmitting(false);
              return;
            }

            const visit = result.visit;
            if (visit) {
              const verb =
                result.action === GymCheckInAction.CheckedIn
                  ? "влезе"
                  : "излезе";
              getSuccessToast(`${visit.userFullName} ${verb} успешно.`);
            }

            setPendingPaymentFor(null);
            setIsPaymentSubmitting(false);
            refreshAll();
          },
          onError: () => {
            setIsPaymentSubmitting(false);
          },
        },
      );
    },
    [mutate, refreshAll, selectedStudioId],
  );

  const handleSelectMember = useCallback((memberId: number) => {
    setSelectedMemberId(memberId);
    setQuery("");
    setDropdownOpen(false);
  }, []);

  const handleNewUserCreated = useCallback(
    async (newUserId: number) => {
      setSelectedMemberId(newUserId);
      setQuery("");
      setDropdownOpen(false);
      await refetchUsers();
    },
    [refetchUsers],
  );

  const handleToggleCheckIn = useCallback(
    (memberId: number) => {
      const member = allUsers?.find((u) => u.id === memberId);
      if (!member) return;
      sendCheckIn(member);
    },
    [allUsers, sendCheckIn],
  );

  const handleConfirmPayment = useCallback(
    (paymentType: PaymentTypes, price: number) => {
      if (!pendingPaymentFor) return;
      setIsPaymentSubmitting(true);
      sendCheckIn(pendingPaymentFor, paymentType, price);
    },
    [pendingPaymentFor, sendCheckIn],
  );

  const handleCancelPayment = useCallback(() => {
    setPendingPaymentFor(null);
    setIsPaymentSubmitting(false);
  }, []);

  const handleRevertLastAction = useCallback(() => {
    const memberId = selectedMemberId;
    const member = allUsers?.find((u) => u.id === memberId) ?? null;
    const last = lastActionData?.lastAction ?? null;
    if (!memberId || !member || !last) return;

    const memberName = getMemberFullName(member);
    const verb =
      last.action === GymCheckInAction.CheckedIn ? "влизането" : "излизането";

    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да отмените ${verb} на ${memberName}?`,
      () => {
        revertMutate(
          {
            endpoint: endpointsMetadata.gym.revertLastAction,
            method: "post",
            payload: { userId: memberId },
            disableSuccessToast: true,
            invalidateKeys: [
              [endpointsMetadata.gym.searchUsers, selectedStudioId],
              [endpointsMetadata.gym.currentlyInside, selectedStudioId],
              [endpointsMetadata.gym.history, selectedStudioId],
              [endpointsMetadata.gym.stats, selectedStudioId],
              [
                endpointsMetadata.gym.lastActionForUser,
                selectedStudioId,
                memberId,
              ],
            ],
          },
          {
            onSuccess: () => {
              const pastVerb =
                last.action === GymCheckInAction.CheckedIn
                  ? "Влизането"
                  : "Излизането";
              getSuccessToast(`${pastVerb} на ${memberName} е отменено.`);
              refreshAll();
            },
          },
        );
      },
    );
  }, [
    selectedMemberId,
    allUsers,
    lastActionData,
    revertMutate,
    refreshAll,
    selectedStudioId,
  ]);

  const handleSetHistoryFilter = useCallback((next: HistoryFilter) => {
    setHistoryFilter(next);
    if (next !== "dateRange") {
      setCustomDateFrom(null);
      setCustomDateTo(null);
    }
  }, []);

  const handlePrevPage = useCallback(() => {
    setHistoryPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setHistoryPage((p) => (historyHasMore ? p + 1 : p));
  }, [historyHasMore]);

  // ─── RFID auto-check-in on 100 % match ────────────────────────────

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(true);

    if (!allUsers) return;

    const normalized = normalizeRfid(q);
    const exactRfidMatch = allUsers.find(
      (u) => u.rfidBandId && normalizeRfid(u.rfidBandId) === normalized,
    );

    if (exactRfidMatch) {
      setSelectedMemberId(exactRfidMatch.id);
      setQuery("");
      setDropdownOpen(false);
      sendCheckIn(exactRfidMatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, allUsers]);

  return {
    stats,
    query,
    setQuery,
    isDropdownOpen,
    setDropdownOpen,
    searchResults,
    selectedMember,
    openVisitForSelected,
    insideVisits,
    historyItems,
    historyTotalCount,
    historyHasMore,
    historyPage,
    historyTotalPages,
    historyFilter,
    customDateFrom,
    customDateTo,
    setCustomDateFrom,
    setCustomDateTo,
    handleSetHistoryFilter,
    handlePrevPage,
    handleNextPage,
    handleSelectMember,
    handleNewUserCreated,
    handleToggleCheckIn,
    handleRevertLastAction,
    canRevertLastAction: !!lastActionData?.lastAction && !isLastActionLoading,
    isRevertLoading: isReverting || isLastActionLoading,
    isCheckInSubmitting,
    pendingPaymentFor,
    isPaymentSubmitting,
    handleConfirmPayment,
    handleCancelPayment,
    paymentOptions,
    // Surfaced so the payment modal can warn that the final price is reduced to the
    // studio's profit share for the card partners (the BE applies the same percentage).
    multisportPercentLeft: studioSettings?.percentLeftAfterMultisportTaxes ?? null,
    coolFitPercentLeft: studioSettings?.percentLeftAfterCoolFitTaxes ?? null,
    isHistoryLoading,
  };
};
